import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { getCurrentUserEmail } from '../../api';
import { ReportsProvider } from '../reports/ReportsContext';
import { useAccess } from '../auth/AccessContext';
import Sidebar from './Sidebar';
import './AppLayout.css';

// Tells a vendor-limited profile what they're looking at, on every page. The
// server filters the data either way; without this, a Meta contractor opening
// "All Cases" would have no way to tell it isn't really all of them.
function ScopeBanner() {
    const { vendorScope } = useAccess();
    if (!vendorScope.scoped) return null;
    const { names } = vendorScope;
    const label = names.length <= 3
        ? names.join(', ')
        : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
    return (
        <p className="layout__scope" role="status">
            <span className="layout__scope-tag">Limited view</span>
            {names.length === 0
                ? <>Your profile is limited to specific vendors, but none are set yet, so reports and
                    marketing pages are empty. Ask an admin to add one.</>
                : <>You're seeing data for <strong>{label}</strong> only.</>}
        </p>
    );
}

// Shared chrome for every signed-in page: a fixed top bar, a collapsible left
// navigation sidebar, and a content area that renders the active route via
// <Outlet />. Wraps everything in ReportsProvider so the sidebar and report
// views share one fetch of the report catalog.
export default function AppLayout() {
    // Controls the sidebar on narrow screens (off-canvas drawer). Always visible
    // on wide screens via CSS.
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <ReportsProvider>
            <div className="layout">
                <header className="layout__topbar">
                    <div className="layout__topbar-left">
                        <button
                            type="button"
                            className="layout__hamburger"
                            aria-label="Toggle navigation"
                            onClick={() => setMobileOpen(open => !open)}
                        >
                            ☰
                        </button>
                        <span className="logo-text">TAX TITANS</span>
                    </div>
                    <div className="layout__topbar-right">
                        <span className="layout__user">{getCurrentUserEmail()}</span>
                        <button className="signOutButton" onClick={() => signOut(auth)}>Sign Out</button>
                    </div>
                </header>

                <div className="layout__body">
                    <aside className={`layout__sidebar ${mobileOpen ? 'layout__sidebar--open' : ''}`}>
                        <Sidebar onNavigate={() => setMobileOpen(false)} />
                    </aside>

                    {mobileOpen && (
                        <div className="layout__scrim" onClick={() => setMobileOpen(false)} aria-hidden="true" />
                    )}

                    <main className="layout__content">
                        <ScopeBanner />
                        <Outlet />
                    </main>
                </div>
            </div>
        </ReportsProvider>
    );
}
